import UserLayout from "@/components/layout/UserPageLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth0Context } from "@/context/Auth0Context";
import { useMe, useUpdateMe } from "@/hooks/user/useMe";
import { useAuth0 } from "@auth0/auth0-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";

// Form validation schema - using function to get translations
const getProfileSchema = (t: any) =>
  z
    .object({
      name: z
        .string()
        .min(1, t("completion.nameRequired"))
        .min(2, t("completion.nameMinLength")),
      title: z.string().min(1, t("completion.titleRequired")),
      customTitle: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (
        data.title === "other" &&
        (!data.customTitle || data.customTitle.trim().length < 2)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["customTitle"],
          message: t("completion.customTitleRequired"),
        });
      }
    });

const ProfileCompletionPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth0Context();
  const { data: dbUser } = useMe();
  const { t } = useTranslation("profile");

  const { mutateAsync: updateUser } = useUpdateMe();
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const profileSchema = getProfileSchema(t);
  type ProfileFormData = z.infer<typeof profileSchema>;

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
    setError,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      title: "",
      customTitle: "",
    },
  });

  const watchedTitle = watch("title");

  const professions = {
    nurse: t("professions.nurse"),
    doctor: t("professions.doctor"),
    paramedic: t("professions.paramedic"),
    other: t("professions.other"),
  };

  const onSubmit = async (data: ProfileFormData) => {
    setIsLoading(true);
    try {
      if (!user?.sub) {
        throw new Error("User ID is missing");
      }

      const translatedTitle =
        data.title === "other"
          ? data.customTitle!.trim()
          : professions[data.title as keyof typeof professions];

      await updateUser({
        id: dbUser?.id || "",
        userData: {
          name: data.name,
          title: translatedTitle, // Save translated text instead of key
        },
        sub: user?.sub || "",
      });
    } catch (error: any) {
      console.error("Error updating profile:", error);
      setError("root", {
        type: "manual",
        message: error?.message || t("completion.errorMessage"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <UserLayout>
      <div className="flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <Card className="shadow-lg">
            <CardHeader className="pb-6 text-center">
              <CardTitle className="text-2xl font-bold text-gray-800">
                {t("completion.greeting")} {user?.given_name || user?.name}!
              </CardTitle>
              <CardDescription className="mt-2 text-base">
                {t("completion.description")}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-base font-medium">
                    {t("completion.nameLabel")} *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    {...register("name")}
                    placeholder={t("completion.namePlaceholder")}
                    disabled={isLoading}
                    className="h-12 text-base"
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600">
                      {errors.name.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-base font-medium">
                    {t("completion.titleLabel")} *
                  </Label>
                  <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                      <Select
                        disabled={isLoading}
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger
                          className="border-black-100 w-full [&_svg]:text-black!"
                          size="md"
                        >
                          <SelectValue
                            placeholder={t("completion.titlePlaceholder")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {Object.entries(professions).map(([key, label]) => (
                              <SelectItem key={key} value={key}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.title && (
                    <p className="text-sm text-red-600">
                      {errors.title.message}
                    </p>
                  )}
                </div>

                {watchedTitle === "other" && (
                  <div className="space-y-2">
                    <Label
                      htmlFor="customTitle"
                      className="text-base font-medium"
                    >
                      {t("completion.customTitleLabel")} *
                    </Label>
                    <Input
                      id="customTitle"
                      type="text"
                      {...register("customTitle")}
                      placeholder={t("completion.customTitlePlaceholder")}
                      disabled={isLoading}
                      className="h-12 text-base"
                    />
                    {errors.customTitle && (
                      <p className="text-sm text-red-600">
                        {errors.customTitle.message}
                      </p>
                    )}
                  </div>
                )}

                {errors.root && (
                  <div className="flex items-center space-x-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                    <span className="font-medium">⚠️</span>
                    <span>{errors.root.message}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  className="h-12 w-full text-base font-medium"
                  disabled={isLoading || !isValid}
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span>{t("completion.saving")}</span>
                    </div>
                  ) : (
                    t("completion.saveAndContinue")
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </UserLayout>
  );
};

export default ProfileCompletionPage;
